// Copilot API client for MCP server calls

const MCP_API_URL = process.env.REACT_APP_MCP_API_URL || 'https://core.yantrai.com/mcp/call';
const API_KEY = process.env.REACT_APP_MCP_API_KEY || 'orxAzLh3iwFfDJNc8PHRUFjdAmkKUdKchWkvKODtnXI';

// Types
export interface ResearchQuery {
  query: string;
  company_names?: string[];
  top_k?: number;
  deep_search?: boolean;
  return_pdf?: boolean;
  pdf_expiry_hours?: number;
  enable_multi_agent?: boolean;
}

export interface ResearchSource {
  type: 'database' | 'web' | 'company_insight';
  company?: string;
  report_type?: string;
  similarity?: number;
  title?: string;
  url?: string;
  insight_type?: string;
}

export interface ResearchResult {
  status: string;
  pdf_url?: string;
  format?: string;
  query?: string;
  companies_covered?: string;
  total_documents?: number;
  pdf_expiry_hours?: number;
  llm_response?: string;
  multi_agent?: boolean;
  workflow_trace?: string[];
  query_type?: string;
  confidence_score?: number;
  db_search_status?: string;
  web_search_status?: string;
  coordinator_reasoning?: string;
  sources?: ResearchSource[];
  error?: string;
}

export interface CompanyInsights {
  business_overview?: string;
  future_plans?: string;
  product_segments?: string;
  suppliers?: string;
  clientele?: string;
  joint_ventures?: string;
  subsidiaries?: string;
  key_management_people_changes?: string;
  management_guidance?: string;
  related_party_transactions?: string;
}

export interface CompanyDetails {
  name: string;
  ticker_symbol?: string;
  sector?: string;
  industry?: string;
  market_cap?: number;
  current_price?: number;
  stock_pe?: number;
  book_value?: number;
  dividend_yield?: number;
  roce?: number;
  roe?: number;
  face_value?: number;
  nse_link?: string;
  // Financial data
  quarterly_results_data?: any;
  yearly_results_data?: any;
  balance_sheets?: any;
  cashflow_statements?: any;
  ratios?: any;
  // Documents
  annual_reports_data?: any[];
  concalls_data?: any[];
  company_announcements_data?: any[];
  // Insights from PostgreSQL
  company_insights?: CompanyInsights;
  [key: string]: any;
}

export interface CompanySearchResult {
  status: string;
  data: CompanyDetails[];
  pagination?: {
    page: number;
    page_size: number;
    has_next: boolean;
  };
  query?: string;
  error?: string;
}

// Base MCP call function
async function callMCP<T>(toolName: string, args: Record<string, any>): Promise<T> {
  const response = await fetch(MCP_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${API_KEY}`,
    },
    body: JSON.stringify({
      name: toolName,
      arguments: args,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}

// Query stock research
export async function queryStockResearch(params: ResearchQuery): Promise<ResearchResult> {
  return callMCP<ResearchResult>('query_stock_research', {
    query: params.query,
    company_names: params.company_names,
    top_k: params.top_k || 5,
    deep_search: params.deep_search || false,
    return_pdf: params.return_pdf ?? true,
    pdf_expiry_hours: params.pdf_expiry_hours || 24,
    enable_multi_agent: params.enable_multi_agent || false,
  });
}

// Get all company names for autocomplete
export async function getAllCompanyNames(): Promise<string[]> {
  const result = await callMCP<{ status: string; data: string[] }>('get_all_companies', {
    page: 1,
    page_size: 10000,
  });

  if (result.status === 'success' && result.data) {
    return result.data.sort();
  }
  return [];
}

// Search companies with filters and pagination
export async function searchCompanies(
  criteria: string,
  page: number = 1,
  pageSize: number = 20
): Promise<CompanySearchResult> {
  return callMCP<CompanySearchResult>('search_companies', {
    criteria: criteria,
    page: page,
    page_size: pageSize,
    sort_by: 'name',
    order: 'asc',
  });
}

// Get company details
export async function getCompanyDetails(companyName: string): Promise<{ status: string; data?: CompanyDetails; error?: string }> {
  return callMCP<{ status: string; data?: CompanyDetails; error?: string }>('get_company_details', {
    company_name: companyName,
  });
}
